/* eslint-disable no-shadow */
import { useState } from 'react';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// graphQL
import { useLazyQuery } from '@apollo/client';

// auth
import { useAuthContext } from '../../../auth/useAuthContext';
// components
import Iconify from '../../../components/iconify';
import FormProvider, { RHFTextField } from '../../../components/hook-form';
import { CLIENT_LOGIN } from '../../../graphQL/queries';

// ----------------------------------------------------------------------

export default function AuthLoginForm() {
  const { login } = useAuthContext();
  const [clientLogin, { loading }] = useLazyQuery(CLIENT_LOGIN, {
    fetchPolicy: 'no-cache',
  });
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email è obbligatoria')
      .email('Email deve essere un indirizzo email valido'),
    password: Yup.string().required('Password è obbligatoria'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const { data: responseData, error: queryErrors } = await clientLogin({
        variables: {
          email: data.email,
          password: data.password,
        },
        fetchPolicy: 'no-cache',
      });

      if (queryErrors) {
        setError('afterSubmit', {
          message: queryErrors?.message,
        });
        return;
      }

      const token = responseData?.clientLogin?.token;

      if (token) {
        await login(token);
        reset();
      } else {
        setError('afterSubmit', {
          message: 'Accesso fallito, nessun token ricevuto.',
        });
        reset();
      }
    } catch (error) {
      console.error(error);
      setError('afterSubmit', {
        message: error.message || 'Si è verificato un errore sconosciuto.',
      });
      reset();
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {errors.afterSubmit ? <Alert severity="error">{errors.afterSubmit.message}</Alert> : null}

        <RHFTextField name="email" label="Indirizzo E-mail" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <span key={showPassword ? 'eye-fill' : 'eye-off-fill'}>
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </span>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting || loading}
        sx={{
          my: 3,
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        Accedi
      </LoadingButton>
    </FormProvider>
  );
}
